import {
  capoFor,
  keyName,
  parseChart,
  parseChartLayout,
  parseKey,
  renderChart,
  shapesKey,
  tagsForKey,
  transposeKey,
  type ChartLayout,
  type ChartLine,
  type Key,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useApi } from '../api-context.tsx'
import { servicesKeys } from './useServices.ts'

/**
 * The Music Stand: a plan's charts, in the key the band is playing, for one
 * player's instrument — and following the leader if they want it to.
 *
 * Offline is the default, not a mode. The whole plan — every chart — is one
 * answer (`/plans/:id/stand`), cached with the rest of the query cache that
 * the web app persists to IndexedDB, and read `offlineFirst`: once it has been
 * opened at home, it opens in a church basement with no signal. Transposition,
 * capo shapes and Nashville numbers are all computed here, from the chart, so
 * none of them needs the network either.
 */

export interface StandOptions {
  /** Where the follow-the-leader socket is, or null to not follow. */
  followUrl: string | null
}

/** A line as the Stand shows it: the chart model, chords already in the playing key. */
export type RenderedLine = ChartLine

export function useMusicStand(planId: string, options: StandOptions) {
  const api = useApi()
  const queryClient = useQueryClient()
  const stand = useQuery({
    queryKey: servicesKeys.stand(planId),
    enabled: planId !== '',
    networkMode: 'offlineFirst',
    // A plan is read for a whole morning; refetch only when online and asked.
    staleTime: 5 * 60_000,
    gcTime: 24 * 60 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).stand.get()
      if (error) throw error
      return data
    },
  })

  const items = stand.data?.items ?? []
  const [index, setIndex] = useState(0)
  const [following, setFollowing] = useState(true)
  const [keys, setKeys] = useState<Record<string, string>>({})
  const [capo, setCapo] = useState(0)
  const [numbers, setNumbers] = useState(false)
  const [leaderItemId, setLeaderItemId] = useState<string | null>(null)
  const [connected, setConnected] = useState(false)

  // Start where the leader is, if a service is already under way.
  useEffect(() => {
    const live = stand.data?.liveItemId
    if (!live) return
    setLeaderItemId(live)
    const at = items.findIndex((item) => item.id === live)
    if (at >= 0) setIndex(at)
  }, [stand.data?.liveItemId])

  // Follow the leader: a WebSocket that says which item they are on.
  useEffect(() => {
    if (!options.followUrl) return
    let socket: WebSocket | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let closed = false
    let attempt = 0
    const connect = () => {
      socket = new WebSocket(options.followUrl ?? '')
      socket.onopen = () => {
        attempt = 0
        setConnected(true)
      }
      socket.onmessage = (event) => {
        let message: { type?: string; itemId?: string | null } = {}
        try {
          message = JSON.parse(String(event.data)) as typeof message
        } catch {
          return
        }
        if (message.type !== 'live') return
        setLeaderItemId(message.itemId ?? null)
      }
      socket.onclose = () => {
        setConnected(false)
        if (closed) return
        attempt += 1
        retry = setTimeout(connect, Math.min(15_000, 500 * 2 ** attempt))
      }
    }
    connect()
    return () => {
      closed = true
      if (retry) clearTimeout(retry)
      socket?.close()
    }
  }, [options.followUrl])

  // Following means: when the leader moves, so do we.
  useEffect(() => {
    if (!following || !leaderItemId) return
    const at = items.findIndex((item) => item.id === leaderItemId)
    if (at >= 0) setIndex(at)
  }, [leaderItemId, following, items.length])

  const current = items[index] ?? null
  const chartKey: Key | null = current?.chartKey ? parseKey(current.chartKey) : null
  const playKeyName = current ? (keys[current.id] ?? current.key ?? current.chartKey) : null
  const playKey: Key | null = playKeyName ? parseKey(playKeyName) : null
  const shapes = playKey && capo > 0 ? shapesKey(playKey, capo) : playKey
  // The arrangement's tagged keys (Eb · Female). A plan cached before they
  // were part of the answer has none.
  const taggedKeys: readonly { key: string; tags: readonly string[] }[] =
    current?.arrangementKeys ?? []
  // The order it is played in (Intro, Verse 1…); none in a plan cached before it was sent.
  const sequence: readonly string[] = current?.sequence ?? []

  // The chart in the key the band is playing, as numbers or capo shapes if
  // asked — whatever format it was typed in (TRANSPOSE KEY lines included).
  const lines: ChartLine[] = current?.chart
    ? renderChart(parseChart(current.chart), {
        from: chartKey,
        to: playKey,
        numbers,
        capo,
      }).lines
    : []
  // Its layout (font, size, chord colour, columns); the defaults in a plan cached before.
  const layout: ChartLayout = parseChartLayout(current?.chartLayout)

  // The leader's controls: moving the room, not just this screen.
  const lead = useMutation({
    mutationFn: async (itemId: string | null) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).live.post({ itemId })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      if (data) setLeaderItemId(data.itemId)
      void queryClient.invalidateQueries({ queryKey: servicesKeys.plan(planId) })
    },
  })

  return {
    plan: stand.data,
    isLoading: stand.isPending,
    error: stand.error,
    /** True when what is on screen came from the cache, not the network. */
    fromCache: stand.isFetched === false && stand.data !== undefined,
    items,
    index,
    current,
    lines,
    layout,
    go: (next: number) => {
      // Moving by hand stops following, as it would on paper.
      setFollowing(false)
      setIndex(Math.max(0, Math.min(items.length - 1, next)))
    },
    following,
    setFollowing,
    connected,
    leaderItemId,
    playKey: playKey ? keyName(playKey) : null,
    /** The playing key's tags on its arrangement ("Female"), if it is one of its keys. */
    playKeyTags: tagsForKey(taggedKeys, playKey ? keyName(playKey) : null),
    /** The arrangement's keys, each with who it is for: offered first. */
    taggedKeys,
    /** The current song's sequence, for its roadmap line. */
    sequence,
    shapesKey: shapes && capo > 0 ? keyName(shapes) : null,
    setKey: (key: string) => current && setKeys((all) => ({ ...all, [current.id]: key })),
    stepKey: (semitones: number) => {
      if (current && playKey)
        setKeys((all) => ({ ...all, [current.id]: keyName(transposeKey(playKey, semitones)) }))
    },
    capo,
    setCapo: (fret: number) => setCapo(Math.max(0, Math.min(11, fret))),
    /** The capo that would let you play this song with the given shapes. */
    capoForShapes: (shapesName: string) => {
      const target = parseKey(shapesName)
      return playKey && target ? capoFor(playKey, target) : null
    },
    numbers,
    setNumbers,
    lead,
  }
}
